import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {testIdProps} from '@github-ui/test-id-props'
import {useSearchParams} from '@github-ui/use-navigate'
import {useMemo} from 'react'

import {EmptyState} from './EmptyState'
import {LabelListPagination} from './LabelListPagination'
import {LabelRow} from './LabelRow'
import {LabelSortMenu} from './LabelSortMenu'
import type {LabelListNode, RepositoryNode} from './repository-label-index-page-query-types'
import styles from './RepositoryLabel.module.css'

export type LabelRepoFragment = RelayCompat<
  Pick<RepositoryNode, 'id' | 'nameWithOwner' | 'isWritable' | 'viewerCanPush' | 'labels'>
>
export type LabelListFragment = RelayCompat<Pick<LabelListNode, 'edges' | 'totalCount'>>

type LabelListProps = {
  repository: LabelRepoFragment
  onCreateLabel?: () => void
  queryKey?: RouteQueryKey
}

type LabelListInternalProps = {
  labels: LabelListFragment
  onCreateLabel?: () => void
  queryKey?: RouteQueryKey
  viewerCanPush: boolean
  isActionsAvailable: boolean
  repositoryId: string
  repositoryNameWithOwner: string
}

export function LabelList({repository, onCreateLabel, queryKey}: LabelListProps) {
  if (!repository.labels) {
    throw new Error('labels data is null or undefined')
  }
  const isActionsAvailable = repository.isWritable && repository.viewerCanPush
  const viewerCanPush = repository.viewerCanPush

  return (
    <LabelListInternal
      labels={repository.labels}
      onCreateLabel={onCreateLabel}
      queryKey={queryKey}
      viewerCanPush={viewerCanPush}
      isActionsAvailable={isActionsAvailable}
      repositoryId={repository.id}
      repositoryNameWithOwner={repository.nameWithOwner}
    />
  )
}

function LabelListInternal({
  labels,
  onCreateLabel,
  queryKey,
  viewerCanPush,
  isActionsAvailable,
  repositoryId,
  repositoryNameWithOwner,
}: LabelListInternalProps) {
  const nodes = useMemo(() => {
    // telay types mark description as possibly undefined, but LabelRow expects `string | null`
    // its temporary until LabelList is also migrated off Relay (https://github.com/github/planning-tracking/issues/3053).
    return (
      labels?.edges
        ?.map(edge => (edge?.node ? {...edge.node, description: edge.node.description ?? null} : null))
        .filter(node => !!node) || []
    )
  }, [labels])
  const [searchParams] = useSearchParams()

  const dataCount = labels?.totalCount || 0
  const metadataTitle = <span aria-live="polite">{dataCount === 1 ? `1 label` : `${dataCount} labels`}</span>

  const actions = useMemo(() => {
    if (nodes.length === 0 || searchParams.get('q')) return []
    else {
      return [
        {
          key: 'label-sort-menu',
          render: () => <LabelSortMenu />,
        },
      ]
    }
  }, [nodes.length, searchParams])

  const listItemsHeader = <ListViewMetadata title={metadataTitle} actions={actions} actionsLabel="Label actions" />

  const labelIds = useMemo(() => nodes.map(node => node.id), [nodes])

  return (
    <div className={styles.labelListWrapper} data-hpc>
      <ListView
        {...testIdProps('repository-label-list-view')}
        title="Labels"
        metadata={listItemsHeader}
        titleHeaderTag="h2"
        // role="status" doesn't add much for the static "no labels in repository" case,
        // but it's useful for the "no matches" case since that message is rendered dynamically after filtering.
        as={nodes.length > 0 ? 'ul' : 'div'}
        role={nodes.length > 0 ? 'list' : 'status'}
      >
        {nodes.length > 0 &&
          nodes.map(node => (
            <LabelRow
              key={node.id}
              label={node}
              isActionsAvailable={isActionsAvailable}
              viewerCanPush={viewerCanPush}
              labelIds={labelIds}
              repositoryId={repositoryId}
              repositoryNameWithOwner={repositoryNameWithOwner}
              queryKey={queryKey}
            />
          ))}
        {nodes.length === 0 && (
          <EmptyState noLabelsCreated={dataCount === 0} viewerCanPush={viewerCanPush} onCreateLabel={onCreateLabel} />
        )}
      </ListView>
      {nodes.length > 0 && <LabelListPagination dataCount={dataCount} />}
    </div>
  )
}
